import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, ClipboardCheck, Filter, Search, Utensils } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useLang } from "../../context/LangContext";
import { useV2Session } from "../context/V2SessionContext";
import { getV2Copy } from "../content";
import { calculateLoggedNutrients, validateLogQuantity } from "../lib/v2FoodLogUtils";
import {
  buildUnifiedFoodList,
  filterFoods,
  FOOD_FILTERS,
  formatFoodServing,
  getFoodCategories,
  getLocalizedFoodName,
  getLocalizedFoodTypeLabel,
} from "../lib/v2FoodUtils";
import { createFoodLog, listCustomFoods } from "../lib/v2Database";
import { createV2FoodLog, loadV2CustomFoods, loadV2MainFoods } from "../lib/v2Storage";

export default function V2FoodLogging() {
  const { lang } = useLang();
  const { isGuest, isRegistered, session, user } = useV2Session();
  const copy = getV2Copy(lang);
  const foodsCopy = copy.foodsCopy;
  const logCopy = copy.foodLoggingCopy;
  const location = useLocation();
  const navigate = useNavigate();
  const query = new URLSearchParams(location.search);
  const foodIdFromUrl = query.get("food_id");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState(FOOD_FILTERS.all);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [customFoods, setCustomFoods] = useState(() => (isRegistered ? [] : loadV2CustomFoods({ isGuest })));
  const [loadingFoods, setLoadingFoods] = useState(false);
  const mainFoods = useMemo(() => loadV2MainFoods(), []);
  const foods = useMemo(() => buildUnifiedFoodList(mainFoods, customFoods), [customFoods, mainFoods]);
  const [selectedFoodId, setSelectedFoodId] = useState(() => foodIdFromUrl || "");
  const [portions, setPortions] = useState("1");
  const [portionsError, setPortionsError] = useState("");

  const selectedFood = useMemo(
    () => foods.find((food) => food.id === selectedFoodId) || null,
    [foods, selectedFoodId]
  );
  const categories = useMemo(() => getFoodCategories(foods), [foods]);
  const filteredFoods = useMemo(
    () => filterFoods(foods, { search, type: typeFilter, category: categoryFilter }),
    [categoryFilter, foods, search, typeFilter]
  );
  const portionsValidation = validateLogQuantity(portions);
  const canSaveSelectedFood = Boolean(selectedFood && portionsValidation.valid && portionsValidation.portions > 0);
  const preview = selectedFood && portionsValidation.valid
    ? calculateLoggedNutrients(selectedFood, portionsValidation.portions)
    : null;

  useEffect(() => {
    let alive = true;

    async function loadCustomFoodList() {
      if (isRegistered && user?.id) {
        setLoadingFoods(true);
        const foods = await listCustomFoods(user.id).catch(() => null);
        if (alive && foods) setCustomFoods(foods);
        if (alive) setLoadingFoods(false);
        return;
      }

      setCustomFoods(loadV2CustomFoods({ isGuest }));
      setLoadingFoods(false);
    }

    loadCustomFoodList();
    return () => {
      alive = false;
    };
  }, [isGuest, isRegistered, user?.id]);

  useEffect(() => {
    if (selectedFood && !portions) {
      setPortions("1");
    }
  }, [selectedFood, portions]);

  const selectFood = (food) => {
    setSelectedFoodId(food.id);
    setSearch("");
    setPortions("1");
    setPortionsError("");
  };

  const handleSave = async () => {
    if (!selectedFood) {
      toast.error(logCopy.selectFoodError);
      return;
    }

    const validation = validateLogQuantity(portions);
    if (!validation.valid) {
      setPortionsError(validation.error);
      toast.error(logCopy.quantityError);
      return;
    }

    const result =
      isRegistered && user?.id
        ? await createFoodLog(user.id, selectedFood, validation.portions).catch(() => ({
            ok: false,
            errors: { form: logCopy.saveError },
            log: null,
          }))
        : createV2FoodLog(selectedFood, validation.portions, {
            isGuest,
            userId: session?.user?.id || "",
          });

    if (!result.ok) {
      setPortionsError(result.errors?.quantity || "");
      toast.error(logCopy.saveError);
      return;
    }

    toast.success(logCopy.saveSuccess);
    navigate("/history");
  };

  return (
    <section className="mx-auto max-w-6xl min-w-0 space-y-6 overflow-x-hidden">
      <header className="space-y-3">
        <Button asChild variant="ghost" className="px-0">
          <Link to="/foods">
            <ArrowLeft className="h-4 w-4" />
            {logCopy.backToFoods}
          </Link>
        </Button>
        <div className="flex items-start gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <ClipboardCheck className="h-5 w-5" />
          </span>
          <div className="space-y-2">
            <p className="text-sm font-semibold text-primary">{copy.brand}</p>
            <h1 className="text-3xl font-extrabold tracking-tight md:text-4xl">{logCopy.title}</h1>
            <p className="max-w-3xl text-muted-foreground">{logCopy.description}</p>
          </div>
        </div>
      </header>

      <div className="grid min-w-0 gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.9fr)]">
        <section className="min-w-0 rounded-lg border border-border bg-card p-5">
          <h2 className="text-lg font-bold">{logCopy.selectFood}</h2>
          <div className="mt-4 grid min-w-0 gap-3 lg:grid-cols-[minmax(0,1fr)_auto_auto] lg:items-center">
            <label className="relative block min-w-0">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="w-full pl-9"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={logCopy.searchPlaceholder}
              />
            </label>

            <div className="flex min-w-0 flex-wrap gap-2">
              <FilterButton active={typeFilter === FOOD_FILTERS.all} onClick={() => setTypeFilter(FOOD_FILTERS.all)}>
                {foodsCopy.all}
              </FilterButton>
              <FilterButton
                active={typeFilter === FOOD_FILTERS.verified}
                onClick={() => setTypeFilter(FOOD_FILTERS.verified)}
              >
                {foodsCopy.verified}
              </FilterButton>
              <FilterButton
                active={typeFilter === FOOD_FILTERS.custom}
                onClick={() => setTypeFilter(FOOD_FILTERS.custom)}
              >
                {foodsCopy.custom}
              </FilterButton>
            </div>

            <label className="flex min-w-0 max-w-full items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm">
              <Filter className="h-4 w-4 shrink-0 text-muted-foreground" />
              <select
                className="min-w-0 max-w-full bg-transparent text-sm outline-none"
                value={categoryFilter}
                onChange={(event) => setCategoryFilter(event.target.value)}
              >
                <option value="all">{foodsCopy.allCategories}</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="mt-4 space-y-2 lg:hidden">
            {selectedFood && (
              <p className="text-sm text-muted-foreground">
                {logCopy.selected}: {getLocalizedFoodName(selectedFood, lang)} · {formatAmount(portionsValidation.portions)} {logCopy.portionShort}
              </p>
            )}
            <Button
              type="button"
              className="w-full"
              onClick={handleSave}
              disabled={!canSaveSelectedFood}
              data-testid="mobile-save-food-log"
            >
              <ClipboardCheck className="h-4 w-4" />
              {logCopy.saveFoodLog}
            </Button>
          </div>
          <div className="mt-4 max-h-[30rem] space-y-2 overflow-y-auto pr-1">
            {filteredFoods.map((food) => (
              <button
                key={`${food.type}-${food.id}`}
                type="button"
                onClick={() => selectFood(food)}
                className={`w-full rounded-lg border p-3 text-left transition-colors ${
                  selectedFoodId === food.id
                    ? "border-primary bg-primary/10"
                    : "border-border bg-background hover:border-primary/50"
                }`}
              >
                <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <span className="block truncate font-bold">{getLocalizedFoodName(food, lang)}</span>
                    <span className="mt-1 block text-sm text-muted-foreground">
                      {food.category} · {getLocalizedFoodTypeLabel(food, foodsCopy)}
                    </span>
                  </div>
                  <span className="shrink-0 text-sm font-semibold text-muted-foreground">
                    {formatFoodServing(food)}
                  </span>
                </div>
              </button>
            ))}
            {filteredFoods.length === 0 && (
              <div className="rounded-lg border border-dashed border-border bg-background p-5 text-center text-sm text-muted-foreground">
                <p className="font-semibold text-foreground">{loadingFoods ? logCopy.loadingFoods : logCopy.noMatchesTitle}</p>
                <p className="mt-1">{logCopy.noMatchesDescription}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button asChild size="sm" variant="outline">
                    <Link to="/foods/add">{foodsCopy.addCustomFood}</Link>
                  </Button>
                  <Button asChild size="sm" variant="outline">
                    <Link to="/mixer">{logCopy.openMixer}</Link>
                  </Button>
                </div>
              </div>
            )}
          </div>
        </section>

        <section className="min-w-0 space-y-5">
          <SelectedFoodCard food={selectedFood} copy={logCopy} foodsCopy={foodsCopy} lang={lang} />

          <div className="rounded-lg border border-border bg-card p-5">
            <h2 className="text-lg font-bold">{logCopy.portionsEaten}</h2>
            <div className="mt-4 space-y-2">
              <Label htmlFor="food-log-portions">{logCopy.portionsLabel}</Label>
              <Input
                id="food-log-portions"
                type="number"
                min="0"
                step="0.1"
                inputMode="decimal"
                placeholder={logCopy.portionsPlaceholder}
                value={portions}
                onChange={(event) => {
                  setPortions(event.target.value);
                  setPortionsError("");
                }}
                disabled={!selectedFood}
              />
              {selectedFood && (
                <p className="text-sm text-muted-foreground">
                  {formatCopy(logCopy.onePortionEquals, { serving: formatFoodServing(selectedFood) })}
                </p>
              )}
              {portionsError && <p className="text-sm font-medium text-destructive">{portionsError}</p>}
            </div>
          </div>

          <PreviewCard preview={preview} selectedFood={selectedFood} copy={logCopy} foodsCopy={foodsCopy} />

          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <Button asChild variant="outline">
              <Link to="/history">{copy.dashboardCopy.viewHistory}</Link>
            </Button>
            <Button type="button" onClick={handleSave} disabled={!selectedFood}>
              <ClipboardCheck className="h-4 w-4" />
              {logCopy.saveFoodLog}
            </Button>
          </div>
        </section>
      </div>
    </section>
  );
}

function SelectedFoodCard({ food, copy, foodsCopy, lang }) {
  if (!food) {
    return (
      <section className="rounded-lg border border-border bg-card p-5">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted text-muted-foreground">
            <Utensils className="h-5 w-5" />
          </span>
          <div>
            <h2 className="text-lg font-bold">{copy.selectedFood}</h2>
            <p className="text-sm text-muted-foreground">{copy.selectedFoodEmpty}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="rounded-lg border border-border bg-card p-5">
      <div className="flex min-w-0 items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="truncate text-lg font-extrabold">{getLocalizedFoodName(food, lang)}</h2>
          <p className="text-sm text-muted-foreground">
            {food.category} · {getLocalizedFoodTypeLabel(food, foodsCopy)}
          </p>
        </div>
        <span className="shrink-0 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-bold text-primary">{getLocalizedFoodTypeLabel(food, foodsCopy)}</span>
      </div>
      <div className="mt-4 rounded-lg border border-border bg-background p-3 text-sm">
        <span className="block text-xs font-medium text-muted-foreground">{copy.listedServing}</span>
        <span className="font-bold">
          {formatFoodServing(food)}
        </span>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
        <Nutrient label={foodsCopy.potassiumMg.replace(" (mg)", "")} value={food.potassium_mg} unit="mg" />
        <Nutrient label={foodsCopy.phosphorusMg.replace(" (mg)", "")} value={food.phosphorus_mg} unit="mg" />
        <Nutrient label={foodsCopy.sodiumMg.replace(" (mg)", "")} value={food.sodium_mg} unit="mg" />
        <Nutrient label={foodsCopy.proteinG.replace(" (g)", "")} value={food.protein_g} unit="g" />
      </div>
    </section>
  );
}

function PreviewCard({ preview, selectedFood, copy, foodsCopy }) {
  return (
    <section className="rounded-lg border border-border bg-card p-5">
      <h2 className="text-lg font-bold">{copy.reviewBeforeSaving}</h2>
      {preview && selectedFood ? (
        <>
          <p className="mt-1 text-sm text-muted-foreground">
            {copy.snapshotNotice}
          </p>
          <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
            <Nutrient label={foodsCopy.potassiumMg.replace(" (mg)", "")} value={preview.potassium_mg_snapshot} unit="mg" />
            <Nutrient label={foodsCopy.phosphorusMg.replace(" (mg)", "")} value={preview.phosphorus_mg_snapshot} unit="mg" />
            <Nutrient label={foodsCopy.sodiumMg.replace(" (mg)", "")} value={preview.sodium_mg_snapshot} unit="mg" />
            <Nutrient label={foodsCopy.proteinG.replace(" (g)", "")} value={preview.protein_g_snapshot} unit="g" />
          </div>
        </>
      ) : (
        <p className="mt-2 rounded-lg bg-muted p-3 text-sm text-muted-foreground">
          {copy.previewEmpty}
        </p>
      )}
    </section>
  );
}

function FilterButton({ active, children, onClick }) {
  return (
    <Button type="button" variant={active ? "default" : "outline"} onClick={onClick}>
      {children}
    </Button>
  );
}

function Nutrient({ label, value, unit }) {
  return (
    <div className="rounded-lg bg-muted px-3 py-2">
      <span className="block text-xs font-medium text-muted-foreground">{label}</span>
      <span className="font-bold">
        {formatAmount(value)} {unit}
      </span>
    </div>
  );
}

function formatCopy(template = "", values = {}) {
  return Object.entries(values).reduce((result, [key, value]) => result.replace(`{${key}}`, value), template);
}

function formatAmount(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "0";
  if (Number.isInteger(numeric)) return String(numeric);
  return numeric.toFixed(1);
}
